import React, { useCallback, useState } from "react";
import { T, useT } from "@transifex/react";
import { NAVIGATOR_HOSTNAME, SPECIALIST_HOSTNAME } from "env-vars";
import { useFeatureFlags } from "context/admin-content";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  CancelButton,
  SubmitButton,
  ErrorMessage,
} from "./styles";
import {
  RadioGroup,
  RadioOption,
  RadioInput,
  RadioLabel,
} from "./decline-styles";

const NAVIGATOR_GUIDELINES = new Set([7]); // testing

const INTEREST_TO_RISK_GUIDELINE: Record<string, number> = {
  testing: 7,
  genetic_testing: 7,
  mri: 5,
  breast_mri: 5,
  lung: 4,
  lung_ct: 4,
  counseling: 6,
  genetic_counseling: 6,
};

function normalizeInterest(interest: string | undefined): string {
  return String(interest ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

interface DeclineReason {
  value: string;
  label: string;
}

function getDeclineReasons(
  t: (str: string) => string
): DeclineReason[] {
  return [
    { value: "Already Completed", label: t("Already Completed") },
    { value: "Insurance", label: t("Insurance") },
    { value: "Time Constraints", label: t("Time Constraints") },
    { value: "Need More Time", label: t("Need More Time") },
    { value: "Other Reason", label: t("Other Reason") },
  ];
}

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
      fill="currentColor"
    />
  </svg>
);

export interface DeclineReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeclined: () => void;
  /** The interest string of the card being declined (e.g. "testing", "mri", "lung") */
  interest: string;
  /** Display title of the card being declined */
  cardTitle: string;
}

export function DeclineReasonModal({
  isOpen,
  onClose,
  onDeclined,
  interest,
  cardTitle,
}: DeclineReasonModalProps) {
  const t = useT();
  const featureFlags = useFeatureFlags();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reasons = getDeclineReasons(t);

  const handleSubmit = useCallback(async () => {
    if (!selectedReason) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const idToken = localStorage.getItem("id_token");
      const patientId = localStorage.getItem("ciq_id") || "";

      const normalized = normalizeInterest(interest);
      const riskGuideline = INTEREST_TO_RISK_GUIDELINE[normalized];
      if (riskGuideline === undefined) {
        setError(t("Unable to submit. Unknown interest type."));
        return;
      }

      const isNavigatorGuideline =
        !featureFlags.removeAccessToNavigatorApp &&
        NAVIGATOR_GUIDELINES.has(riskGuideline);

      const personContactHistory = {
        actionDateTime: Math.floor(Date.now()),
        actionType: isNavigatorGuideline ? 17 : null,
        in_person_reason: "declined_appointment",
        decline_reason: selectedReason,
        ...(isNavigatorGuideline
          ? {}
          : { risk_guideline: riskGuideline, method: 4 }),
      };

      const personStatus = isNavigatorGuideline
        ? { status: "In Person: Declined" }
        : { status: "Declined", risk_guideline: riskGuideline };

      const endpoint = isNavigatorGuideline
        ? NAVIGATOR_HOSTNAME
        : SPECIALIST_HOSTNAME;
      const method = isNavigatorGuideline ? "POST" : "PUT";

      const response = await fetch(
        `${endpoint}/api/v1/people/${patientId}`,
        {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            person: {
              person_statuses_attributes: [personStatus],
              person_contact_histories_attributes: [personContactHistory],
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit decline");
      }

      setSelectedReason(null);
      onDeclined();
    } catch (err) {
      console.error("Error submitting decline reason:", err);
      setError(t("An error occurred. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedReason, interest, featureFlags.removeAccessToNavigatorApp, onDeclined, t]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const handleClose = useCallback(() => {
    setSelectedReason(null);
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <ModalOverlay
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="decline-reason-title"
      data-testid="DeclineReasonModal"
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle id="decline-reason-title">
            <T _str="Please select a reason" />
          </ModalTitle>
          <ModalSubtitle>
            <T
              _str="Why are you not interested in {cardTitle}?"
              cardTitle={cardTitle}
            />
          </ModalSubtitle>
          <CloseButton
            onClick={handleClose}
            aria-label={t("Close")}
            data-testid="DeclineReasonModal__CloseButton"
          >
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <RadioGroup>
            {reasons.map((reason) => (
              <RadioOption key={reason.value}>
                <RadioInput
                  type="radio"
                  name="declineReason"
                  value={reason.value}
                  checked={selectedReason === reason.value}
                  onChange={() => setSelectedReason(reason.value)}
                  id={`decline-reason-${reason.value}`}
                  data-testid={`DeclineReasonModal__Option-${reason.value}`}
                />
                <RadioLabel htmlFor={`decline-reason-${reason.value}`}>
                  {reason.label}
                </RadioLabel>
              </RadioOption>
            ))}
          </RadioGroup>

          {error && (
            <ErrorMessage data-testid="DeclineReasonModal__Error">
              {error}
            </ErrorMessage>
          )}
        </ModalBody>

        <ModalFooter>
          <CancelButton
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="DeclineReasonModal__CancelButton"
          >
            <T _str="Cancel" />
          </CancelButton>
          <SubmitButton
            type="button"
            onClick={handleSubmit}
            disabled={!selectedReason || isSubmitting}
            data-testid="DeclineReasonModal__SubmitButton"
          >
            {isSubmitting ? t("Submitting...") : t("Ok")}
          </SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
}
